import React from 'react';
import { 
  Bell, Menu, RefreshCw, Shield, Settings, 
  User, LogOut
} from 'lucide-react';

interface ModernHeaderProps {
  profile: any;
  signOut: () => void;
  setSidebarOpen: (open: boolean) => void;
  onRefresh: () => void;
}

const ModernHeader: React.FC<ModernHeaderProps> = ({ 
  profile, 
  signOut, 
  setSidebarOpen, 
  onRefresh 
}) => {
  const handleSignOut = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      try {
        await signOut();
      } catch (error) {
        console.error('Erreur déconnexion:', error);
        alert('Erreur lors de la déconnexion');
      }
    }
  };

  return (
    <div className="sticky top-0 z-40 w-full">
      <div className="flex h-14 sm:h-16 items-center justify-between border-b border-gray-200/20 bg-white/70 backdrop-blur-xl px-4 shadow-sm lg:px-8">
        <div className="flex items-center">
          <button
            type="button"
            className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          
          <div className="flex items-center space-x-3 sm:space-x-4 ml-2 lg:ml-0">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl sm:rounded-2xl blur opacity-75"></div>
              <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 w-8 h-8 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <Shield className="text-white text-sm sm:text-lg" />
              </div>
            </div>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Focal Audit Pro
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 font-medium hidden sm:block">Administration Hub</p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          <button className="relative p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-xl transition-all">
            <Bell className="h-4 w-4 sm:h-6 sm:w-6" />
            <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
          </button>
          
          <button
            onClick={onRefresh}
            className="hidden sm:flex items-center space-x-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="hidden md:block">Actualiser</span>
          </button>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs sm:text-sm font-semibold text-gray-900">{profile?.full_name || 'Admin Principal'}</p>
              <p className="text-xs text-gray-500">En ligne</p>
            </div>
            
            <div className="relative group">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center cursor-pointer">
                <span className="text-white font-semibold text-xs sm:text-sm">
                  {profile?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'AP'}
                </span>
              </div>
              <div className="absolute -bottom-0 -right-0 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 border-2 border-white rounded-full"></div>
              
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-3 border-b border-gray-100">
                  <p className="font-semibold text-gray-900 text-sm">{profile?.full_name}</p>
                  <p className="text-xs text-gray-500">{profile?.email}</p>
                  <span className="inline-flex items-center px-2 py-1 mt-1 bg-purple-100 text-purple-800 text-xs font-bold rounded-full">
                    👑 {profile?.role === 'admin' ? 'Administrateur' : 'Auditeur'}
                  </span>
                </div>
                
                <div className="p-2">
                  <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>Mon Profil</span>
                  </button>
                  
                  <button className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                    <Settings className="w-4 h-4 text-gray-400" />
                    <span>Paramètres</span>
                  </button>
                  
                  <hr className="my-2" />
                  
                  <button 
                    onClick={handleSignOut}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Se déconnecter</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernHeader;